import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Switch,
  Slider,
  InputNumber,
  Button,
  Radio,
  Tag,
  Typography,
  Divider,
  Progress,
  Table,
  Badge,
  Tooltip,
  Alert,
  Space,
  Collapse,
  Spin,
  Select,
  message,
} from "antd";
import {
  DashboardOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  AlertOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  StockOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  SlidersOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import * as settingsService from "../../services/settingsService";
import Loadingpage from "../Loadingpage/Loadingpage";

const { Title, Text, Paragraph } = Typography;

// State Color Tokens
const STATE_COLORS = {
  BALANCED: {
    badge: "success",
    color: "#10B981",
    bgColor: "rgba(16, 185, 129, 0.12)",
    borderColor: "rgba(16, 185, 129, 0.35)",
    label: "BALANCED",
    title: "Operating Naturally On Target",
  },
  HOUSE_PRESERVATION: {
    badge: "warning",
    color: "#F59E0B",
    bgColor: "rgba(245, 158, 11, 0.12)",
    borderColor: "rgba(245, 158, 11, 0.35)",
    label: "HOUSE PRESERVATION",
    title: "Protecting House Margin (High Player Wins)",
  },
  PLAYER_RETENTION: {
    badge: "processing",
    color: "#06B6D4",
    bgColor: "rgba(6, 182, 212, 0.12)",
    borderColor: "rgba(6, 182, 212, 0.35)",
    label: "PLAYER RETENTION",
    title: "Mitigating Player Churn (High Player Losses)",
  },
  DISABLED: {
    badge: "default",
    color: "#64748B",
    bgColor: "rgba(100, 116, 139, 0.12)",
    borderColor: "rgba(100, 116, 139, 0.35)",
    label: "ENGINE PAUSED",
    title: "Pure Natural Market Settlement Active",
  },
};

const RtpEngineSettings = () => {
  const [messageApi, contextHolder] = message.useMessage();

  // Primary State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeWindow, setActiveWindow] = useState("24h");
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30); // 0 = off, 15, 30, 60
  const [auditFilter, setAuditFilter] = useState("all");

  // Backend Data
  const [settings, setSettings] = useState({
    enabled: true,
    targetRtp: 94.0,
    tolerance: 1.5,
    trackingWindow: "24h",
    whaleThreshold: 500,
    minSampleSize: 50,
    maxInterventionPercentage: 15,
    borderlineThresholdPips: 2.5,
    spinSyncEnabled: true,
    multiplierSyncEnabled: true,
    multiplierDampener: 0.70,
    multiplierBooster: 1.30,
  });
  const [initialSettings, setInitialSettings] = useState(null);


  const [analytics, setAnalytics] = useState({
    state: "BALANCED",
    reason: "Initializing RTP tracking...",
    interventionActive: false,
    metrics: {
      totalTurnover: 0,
      totalPayout: 0,
      realizedRtp: 94.0,
      netProfit: 0,
      totalTradesCount: 0,
      wonTradesCount: 0,
      lostTradesCount: 0,
      houseMarginPercent: 6.0,
      window: "24h",
    },
  });

  const [historyData, setHistoryData] = useState([]);
  const [auditTrades, setAuditTrades] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Dirty detection
  const hasChanges = useMemo(() => {
    if (!initialSettings) return false;
    return JSON.stringify(settings) !== JSON.stringify(initialSettings);
  }, [settings, initialSettings]);

  // Calculated corridor & house margin
  const houseMargin = useMemo(() => {
    const rtp = Number(settings.targetRtp) || 94.0;
    return Math.max(0, 100 - rtp).toFixed(2);
  }, [settings.targetRtp]);

  const toleranceLower = useMemo(() => {
    const target = Number(settings.targetRtp) || 94.0;
    const tol = Number(settings.tolerance) || 1.5;
    return (target - tol).toFixed(2);
  }, [settings.targetRtp, settings.tolerance]);

  const toleranceUpper = useMemo(() => {
    const target = Number(settings.targetRtp) || 94.0;
    const tol = Number(settings.tolerance) || 1.5;
    return (target + tol).toFixed(2);
  }, [settings.targetRtp, settings.tolerance]);

  // Current engine state styling token
  const currentStateToken = useMemo(() => {
    if (!settings.enabled) return STATE_COLORS.DISABLED;
    return STATE_COLORS[analytics.state] || STATE_COLORS.BALANCED;
  }, [settings.enabled, analytics.state]);

  // ==========================================
  // Fetch Handlers
  // ==========================================
  const fetchAllData = useCallback(
    async (forceRecalc = false, silent = false) => {
      if (!silent) setRefreshing(true);
      try {
        const [settingsRes, analyticsRes, historyRes, tradesRes] =
          await Promise.all([
            settingsService.getRtpSettings(),
            settingsService.getRtpAnalytics(activeWindow, forceRecalc),
            settingsService.getRtpHistory(activeWindow, 30),
            settingsService.getRtpAuditTrades(auditFilter, 50),
          ]);

        if (settingsRes?.success && settingsRes.result) {
          setSettings(settingsRes.result);
          if (!initialSettings) {
            setInitialSettings(settingsRes.result);
          }
        }

        if (analyticsRes?.success && analyticsRes.result) {
          setAnalytics(analyticsRes.result);
        }

        if (historyRes?.success && Array.isArray(historyRes.result)) {
          // Format for Recharts
          const formatted = historyRes.result.map((item, idx) => ({
            index: idx + 1,
            time: item.createdAt
              ? new Date(item.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : `#${idx + 1}`,
            realizedRtp: Number(item.realizedRtp || 0).toFixed(2),
            targetRtp: Number(settingsRes?.result?.targetRtp || 94.0),
            lowerBand: Number(
              (settingsRes?.result?.targetRtp || 94.0) -
                (settingsRes?.result?.tolerance || 1.5)
            ).toFixed(2),
            upperBand: Number(
              (settingsRes?.result?.targetRtp || 94.0) +
                (settingsRes?.result?.tolerance || 1.5)
            ).toFixed(2),
            turnover: Number(item.totalTurnover || 0),
            payout: Number(item.totalPayout || 0),
            netProfit: Number(item.netProfit || 0),
            state: item.state || "BALANCED",
          }));
          setHistoryData(formatted);
        }

        if (tradesRes?.success && Array.isArray(tradesRes.result)) {
          setAuditTrades(tradesRes.result);
        }
      } catch (err) {
        console.error("Failed to load RTP Engine data:", err);
        if (!silent) {
          messageApi.error("Failed to fetch RTP Engine data");
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeWindow, auditFilter, initialSettings, messageApi]
  );

  // Initial fetch on mount
  useEffect(() => {
    fetchAllData(false);
  }, [fetchAllData]);

  // Window change fetch
  const handleWindowChange = async (newWindow) => {
    setActiveWindow(newWindow);
    setRefreshing(true);
    try {
      const analyticsRes = await settingsService.getRtpAnalytics(newWindow, true);
      if (analyticsRes?.success && analyticsRes.result) {
        setAnalytics(analyticsRes.result);
      }
      const historyRes = await settingsService.getRtpHistory(newWindow, 30);
      if (historyRes?.success && Array.isArray(historyRes.result)) {
        const formatted = historyRes.result.map((item, idx) => ({
          index: idx + 1,
          time: item.createdAt
            ? new Date(item.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : `#${idx + 1}`,
          realizedRtp: Number(item.realizedRtp || 0).toFixed(2),
          targetRtp: Number(settings.targetRtp || 94.0),
          turnover: Number(item.totalTurnover || 0),
          payout: Number(item.totalPayout || 0),
          netProfit: Number(item.netProfit || 0),
          state: item.state || "BALANCED",
        }));
        setHistoryData(formatted);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Filter change fetch for audit trades
  const handleAuditFilterChange = async (filterKey) => {
    setAuditFilter(filterKey);
    setAuditLoading(true);
    try {
      const res = await settingsService.getRtpAuditTrades(filterKey, 50);
      if (res?.success && Array.isArray(res.result)) {
        setAuditTrades(res.result);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAuditLoading(false);
    }
  };

  // Auto-refresh timer
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;
    const interval = setInterval(() => {
      fetchAllData(false, true);
    }, autoRefreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchAllData]);

  // ==========================================
  // Form Save Handler
  // ==========================================
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await settingsService.updateRtpSettings({
        enabled: settings.enabled,
        targetRtp: settings.targetRtp,
        tolerance: settings.tolerance,
        trackingWindow: settings.trackingWindow,
        whaleThreshold: settings.whaleThreshold,
        minSampleSize: settings.minSampleSize,
        maxInterventionPercentage: settings.maxInterventionPercentage,
        borderlineThresholdPips: settings.borderlineThresholdPips,
        spinSyncEnabled: settings.spinSyncEnabled,
        multiplierSyncEnabled: settings.multiplierSyncEnabled,
        multiplierDampener: settings.multiplierDampener,
        multiplierBooster: settings.multiplierBooster,
      });


      if (res?.success && res.result) {
        setSettings(res.result);
        setInitialSettings(res.result);
        messageApi.success("RTP Engine settings updated successfully!");
        // Force refresh analytics with new parameters
        fetchAllData(true, true);
      } else {
        messageApi.error(res?.message || "Failed to update settings");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("An error occurred while saving RTP settings");
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    if (initialSettings) {
      setSettings(initialSettings);
      messageApi.info("Settings reverted to saved values");
    }
  };

  const handleSetPreset = (target, tolerance = 1.5) => {
    setSettings((prev) => ({
      ...prev,
      targetRtp: target,
      tolerance: tolerance,
    }));
  };

  if (loading) {
    return <Loadingpage />;
  }

  // Interventions ratio
  const totalTrades = analytics?.metrics?.totalTradesCount || 0;
  const wonTrades = analytics?.metrics?.wonTradesCount || 0;
  const realizedRtp = Number(analytics?.metrics?.realizedRtp || 0);
  const targetRtp = Number(settings.targetRtp || 94.0);
  const rtpDelta = realizedRtp - targetRtp;
  const netProfit = Number(analytics?.metrics?.netProfit || 0);

  // Audit columns
  const auditColumns = [
    {
      title: "Order ID",
      dataIndex: "clientOrderId",
      key: "clientOrderId",
      render: (text, r) => {
        const id = text || r._id || "-";
        return (
          <span style={{ color: "#CBD5E1", fontSize: "11px", fontFamily: "monospace" }}>
            {id !== "-" && id.length > 8 ? id.slice(-8).toUpperCase() : id}
          </span>
        );
      },
    },
    {
      title: "Pair",
      dataIndex: "pair",
      key: "pair",
      render: (text, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ color: "#FFFFFF", fontWeight: 700, fontSize: "13px" }}>
            {text || "-"}
          </span>
          <span style={{ fontSize: "11px", color: "#FFA07A" }}>
            {record.pair_type || record.type || "CRYPTO"}
          </span>
        </div>
      ),
    },
    {
      title: "Stake",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => {
        const num = Number(amount || 0);
        const isWhale = num >= Number(settings.whaleThreshold || 500);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ color: isWhale ? "#F59E0B" : "#FFFFFF", fontWeight: 700, fontFamily: "monospace", fontSize: "13px" }}>
              ${num.toFixed(2)}
            </span>
            {isWhale && (
              <Tag color="gold" style={{ fontSize: "10px", margin: 0, fontWeight: 700 }}>
                WHALE
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Direction",
      dataIndex: "direction",
      key: "direction",
      render: (dir, r) => {
        const val = String(r.direction || r.type || r.prediction || dir || "").toLowerCase();
        const isUp = val === "up";
        return (
          <Tag
            color={isUp ? "green" : "red"}
            style={{ fontWeight: 700, minWidth: "48px", textAlign: "center", fontSize: "12px" }}
          >
            {val ? val.toUpperCase() : "UP"}
          </Tag>
        );
      },
    },
    {
      title: "Strike / Exit Price",
      key: "prices",
      render: (_, r) => (
        <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
          <div>
            <span style={{ color: "#FFA07A", fontWeight: 600 }}>In: </span>
            <span style={{ color: "#FFFFFF", fontFamily: "monospace" }}>{r.start_price || r.startPrice || "-"}</span>
          </div>
          <div>
            <span style={{ color: "#38BDF8", fontWeight: 600 }}>Out: </span>
            <span style={{ color: "#FFFFFF", fontFamily: "monospace", fontWeight: 700 }}>{r.end_price || r.endPrice || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Distance",
      dataIndex: "pipsDiff",
      key: "pipsDiff",
      render: (pips, r) => {
        const val = Number(pips !== undefined ? pips : (r.pips_diff || 0));
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", fontFamily: "monospace", color: "#FFFFFF", fontWeight: 600 }}>
              {val.toFixed(2)} pips
            </span>
            {r.isBorderline && (
              <Tag color="cyan" style={{ fontSize: "10px", margin: 0, fontWeight: 700 }}>
                BORDERLINE
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Raw -> Result",
      key: "outcome",
      render: (_, r) => {
        const won = r.result === "won";
        const wasAdjusted = Boolean(r.isRtpAdjusted);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
            <Tag
              color={won ? "green" : "red"}
              style={{ fontWeight: 700, minWidth: "50px", textAlign: "center" }}
            >
              {won ? "WON" : "LOSS"}
            </Tag>
            {wasAdjusted && (
              <Tag color="volcano" style={{ fontSize: "10px", margin: 0, fontWeight: 700 }}>
                RTP BALANCED
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Engine State At Settlement",
      dataIndex: "rtpStateAtSettlement",
      key: "rtpStateAtSettlement",
      render: (st) => {
        const stateKey = st || "BALANCED";
        const token = STATE_COLORS[stateKey] || STATE_COLORS.BALANCED;
        return (
          <Tag
            style={{
              backgroundColor: token.bgColor,
              borderColor: token.borderColor,
              color: token.color,
              fontWeight: 700,
              fontSize: "11px",
            }}
          >
            {token.label}
          </Tag>
        );
      },
    },
  ];

  return (
    <div className="rtp-engine-page" style={{ padding: "4px 0 24px 0", color: "#F1F5F9" }}>
      {/* Scoped Styles for RTP Engine Page matching EZ5050 Admin Theme */}
      <style>{`
        .rtp-engine-page {
          color: #F1F5F9;
        }
        .rtp-engine-page .ant-card {
          background: linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%) !important;
          border: 1px solid rgba(252, 68, 32, 0.45) !important;
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-card-head {
          border-bottom: 1px solid rgba(252, 68, 32, 0.35) !important;
        }
        .rtp-engine-page .ant-card-head-title {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-typography {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-typography-secondary {
          color: #CBD5E1 !important;
        }
        .rtp-engine-page .ant-input-number {
          background-color: #1f0805 !important;
          border: 1px solid rgba(252, 68, 32, 0.45) !important;
          color: #ffffff !important;
          border-radius: 6px;
        }
        .rtp-engine-page .ant-input-number-input {
          color: #ffffff !important;
          background: transparent !important;
          font-family: monospace;
          font-weight: 600;
        }
        .rtp-engine-page .ant-input-number-handler-wrap {
          background: #2a0b06 !important;
          border-left: 1px solid rgba(252, 68, 32, 0.3) !important;
        }
        .rtp-engine-page .ant-input-number-handler {
          border-color: rgba(252, 68, 32, 0.3) !important;
          color: #fc4420 !important;
        }
        .rtp-engine-page .ant-input-number-handler:hover .ant-input-number-handler-up-inner,
        .rtp-engine-page .ant-input-number-handler:hover .ant-input-number-handler-down-inner {
          color: #fc4420 !important;
        }
        .rtp-engine-page .ant-radio-button-wrapper {
          background: #1a0604 !important;
          border-color: rgba(252, 68, 32, 0.4) !important;
          color: #CBD5E1 !important;
        }
        .rtp-engine-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          background: #fc4420 !important;
          border-color: #fc4420 !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          box-shadow: 0 0 10px rgba(252, 68, 32, 0.5) !important;
        }
        .rtp-engine-page .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
          background-color: #fc4420 !important;
        }
        .rtp-engine-page .ant-radio-button-wrapper:hover {
          color: #fc4420 !important;
        }
        .rtp-engine-page .ant-select:not(.ant-select-customize-input) .ant-select-selector {
          background-color: #1a0604 !important;
          border: 1px solid rgba(252, 68, 32, 0.4) !important;
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-select-selection-item {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-select-arrow {
          color: #fc4420 !important;
        }
        .rtp-engine-page .ant-collapse {
          background-color: rgba(252, 68, 32, 0.05) !important;
          border: 1px solid rgba(252, 68, 32, 0.3) !important;
        }
        .rtp-engine-page .ant-collapse-header {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-collapse-content {
          background-color: transparent !important;
          border-top: 1px solid rgba(252, 68, 32, 0.25) !important;
        }
        .rtp-engine-page .ant-table {
          background: transparent !important;
        }
        .rtp-engine-page .ant-table-thead > tr > th {
          background: linear-gradient(180deg, #2a0b06 0%, #150302 100%) !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-bottom: 1px solid rgba(252, 68, 32, 0.5) !important;
        }
        .rtp-engine-page .ant-table-tbody > tr > td {
          background: #0d0202 !important;
          color: #ffffff !important;
          border-bottom: 1px solid rgba(252, 68, 32, 0.15) !important;
        }
        .rtp-engine-page .ant-table-tbody > tr:hover > td {
          background: rgba(252, 68, 32, 0.12) !important;
        }
        .rtp-engine-page .pagination-clr .ant-pagination-item,
        .rtp-engine-page .ant-pagination .ant-pagination-item {
          background: #1a0604 !important;
          border-color: rgba(252, 68, 32, 0.4) !important;
        }
        .rtp-engine-page .ant-pagination .ant-pagination-item a {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-pagination .ant-pagination-item-active {
          background: #fc4420 !important;
          border-color: #fc4420 !important;
        }
        .rtp-engine-page .ant-pagination .ant-pagination-item-active a {
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-pagination .ant-pagination-prev .ant-pagination-item-link,
        .rtp-engine-page .ant-pagination .ant-pagination-next .ant-pagination-item-link {
          background: #1a0604 !important;
          border-color: rgba(252, 68, 32, 0.4) !important;
          color: #ffffff !important;
        }
        .rtp-engine-page .ant-pagination-total-text {
          color: #CBD5E1 !important;
        }
      `}</style>
      {contextHolder}

      {/* ========================================================= */}
      {/* 1. HERO ENGINE STATE BANNER */}
      {/* ========================================================= */}
      <Card
        style={{
          marginBottom: "20px",
          background: "linear-gradient(180deg, rgba(252, 68, 32, 0.16) 0%, rgba(20, 5, 3, 0.98) 100%)",
          borderColor: currentStateToken.borderColor || "rgba(252, 68, 32, 0.55)",
          boxShadow: `0 4px 24px rgba(0, 0, 0, 0.7), 0 0 16px ${currentStateToken.bgColor}`,
          borderRadius: "14px",
        }}
        bodyStyle={{ padding: "20px 24px" }}
      >
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col xs={24} xl={14}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Status Header */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Badge
                  status={currentStateToken.badge}
                  text={
                    <Text
                      strong
                      style={{
                        fontSize: "18px",
                        color: currentStateToken.color,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {currentStateToken.label}
                    </Text>
                  }
                />
                <Tag
                  color={settings.enabled ? "green" : "default"}
                  style={{ fontWeight: 600, borderRadius: "6px" }}
                >
                  {settings.enabled ? "AUTOMATED ACTIVE" : "ENGINE PAUSED"}
                </Tag>
                <Tag
                  color="blue"
                  style={{ borderRadius: "6px", fontWeight: 500 }}
                >
                  Window: {activeWindow.toUpperCase()}
                </Tag>
              </div>

              {/* Status Description */}
              <Paragraph
                style={{
                  margin: "0",
                  color: "#CBD5E1",
                  fontSize: "13px",
                  lineHeight: "1.5",
                }}
              >
                {analytics.reason || currentStateToken.title}
              </Paragraph>

              {/* Corridor Range Bar */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "12px",
                  fontSize: "12px",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  width: "fit-content",
                  border: "1px solid rgba(252, 68, 32, 0.35)",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ color: "#CBD5E1", whiteSpace: "nowrap" }}>
                  Corridor Band: [
                  <span style={{ color: "#38BDF8", fontWeight: 600 }}>{toleranceLower}%</span>
                  {" ── "}
                  <strong style={{ color: "#FFF" }}>{targetRtp}%</strong>
                  {" ── "}
                  <span style={{ color: "#38BDF8", fontWeight: 600 }}>{toleranceUpper}%</span>
                  ]
                </span>
                <Divider type="vertical" style={{ borderColor: "rgba(252, 68, 32, 0.35)", margin: "0" }} />
                <span style={{ color: currentStateToken.color, fontWeight: 700, whiteSpace: "nowrap" }}>
                  Realized: {realizedRtp.toFixed(2)}% (
                  {rtpDelta >= 0 ? `+${rtpDelta.toFixed(2)}%` : `${rtpDelta.toFixed(2)}%`}
                  )
                </span>
              </div>
            </div>
          </Col>

          {/* Action Bar */}
          <Col xs={24} xl={10} style={{ textAlign: "right" }}>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Radio.Group
                value={activeWindow}
                onChange={(e) => handleWindowChange(e.target.value)}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="24h">24 Hours</Radio.Button>
                <Radio.Button value="7d">7 Days</Radio.Button>
                <Radio.Button value="all">All-Time</Radio.Button>
              </Radio.Group>

              <Select
                value={autoRefreshInterval}
                onChange={setAutoRefreshInterval}
                style={{ width: 115 }}
                size="middle"
                options={[
                  { label: "Auto: Off", value: 0 },
                  { label: "Auto: 15s", value: 15 },
                  { label: "Auto: 30s", value: 30 },
                  { label: "Auto: 60s", value: 60 },
                ]}
              />

              <Button
                icon={<SyncOutlined spin={refreshing} />}
                onClick={() => fetchAllData(true)}
                type="default"
                style={{
                  borderColor: "rgba(252, 68, 32, 0.5)",
                  backgroundColor: "rgba(252, 68, 32, 0.15)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                }}
              >
                Recalculate
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ========================================================= */}
      {/* 2. EXECUTIVE KPI CARDS */}
      {/* ========================================================= */}
      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        {/* Total Turnover */}
        <Col xs={24} sm={12} xl={6}>
          <Card
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: "rgba(252, 68, 32, 0.45)",
              borderRadius: "12px",
              height: "100%",
            }}
            bodyStyle={{ padding: "18px 20px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "#FFA07A", fontWeight: 700, letterSpacing: "0.5px" }}>
                  TOTAL TURNOVER (VOLUME)
                </span>
                <StockOutlined style={{ color: "#FC4420", fontSize: "18px" }} />
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  fontFamily: "monospace",
                  lineHeight: "1.2",
                  margin: "4px 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                ${Number(analytics?.metrics?.totalTurnover || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div style={{ fontSize: "12px", color: "#CBD5E1" }}>
                Total settled:{" "}
                <strong style={{ color: "#FFFFFF" }}>{totalTrades.toLocaleString()} trades</strong>
              </div>
            </div>
          </Card>
        </Col>

        {/* Total Payouts */}
        <Col xs={24} sm={12} xl={6}>
          <Card
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: "rgba(252, 68, 32, 0.45)",
              borderRadius: "12px",
              height: "100%",
            }}
            bodyStyle={{ padding: "18px 20px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "#FFA07A", fontWeight: 700, letterSpacing: "0.5px" }}>
                  TOTAL PAYOUTS RETURNED
                </span>
                <DollarOutlined style={{ color: "#F59E0B", fontSize: "18px" }} />
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  fontFamily: "monospace",
                  lineHeight: "1.2",
                  margin: "4px 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                ${Number(analytics?.metrics?.totalPayout || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div style={{ fontSize: "12px", color: "#CBD5E1" }}>
                Won:{" "}
                <strong style={{ color: "#FFFFFF" }}>
                  {wonTrades.toLocaleString()} (
                  {totalTrades > 0
                    ? ((wonTrades / totalTrades) * 100).toFixed(1)
                    : 0}
                  %)
                </strong>
              </div>
            </div>
          </Card>
        </Col>

        {/* Realized RTP */}
        <Col xs={24} sm={12} xl={6}>
          <Card
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: currentStateToken.borderColor || "rgba(252, 68, 32, 0.45)",
              borderRadius: "12px",
              height: "100%",
            }}
            bodyStyle={{ padding: "18px 20px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "#FFA07A", fontWeight: 700, letterSpacing: "0.5px" }}>
                  CURRENT REALIZED RTP
                </span>
                <DashboardOutlined
                  style={{ color: currentStateToken.color, fontSize: "18px" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "4px 0" }}>
                <span
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: currentStateToken.color,
                    fontFamily: "monospace",
                    lineHeight: "1.2",
                    whiteSpace: "nowrap",
                  }}
                >
                  {realizedRtp.toFixed(2)}%
                </span>
                <Tag
                  color={
                    Math.abs(rtpDelta) <= Number(settings.tolerance || 1.5)
                      ? "success"
                      : rtpDelta > 0
                      ? "warning"
                      : "processing"
                  }
                  style={{ fontSize: "11px", margin: "0", fontWeight: 700 }}
                >
                  {rtpDelta >= 0 ? `+${rtpDelta.toFixed(2)}%` : `${rtpDelta.toFixed(2)}%`}
                </Tag>
              </div>
              <Progress
                percent={Math.min(100, Math.max(0, realizedRtp))}
                showInfo={false}
                strokeColor={currentStateToken.color}
                trailColor="rgba(252, 68, 32, 0.2)"
                size="small"
                style={{ margin: "2px 0 0 0" }}
              />
            </div>
          </Card>
        </Col>

        {/* Guaranteed Platform Gross Profit */}
        <Col xs={24} sm={12} xl={6}>
          <Card
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: "rgba(252, 68, 32, 0.45)",
              borderRadius: "12px",
              height: "100%",
            }}
            bodyStyle={{ padding: "18px 20px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "#FFA07A", fontWeight: 700, letterSpacing: "0.5px" }}>
                  PLATFORM GROSS PROFIT
                </span>
                <SafetyCertificateOutlined
                  style={{ color: "#10B981", fontSize: "18px" }}
                />
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: netProfit >= 0 ? "#10B981" : "#EF4444",
                  fontFamily: "monospace",
                  lineHeight: "1.2",
                  margin: "4px 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {netProfit >= 0
                  ? `+$${netProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `-$${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
              <div style={{ fontSize: "12px", color: "#CBD5E1" }}>
                House Margin:{" "}
                <strong style={{ color: "#10B981" }}>
                  {analytics?.metrics?.houseMarginPercent?.toFixed(2) || "6.00"}%
                </strong>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ========================================================= */}
      {/* 3. CORE CONTROLS & RECHARTS CONVERGENCE */}
      {/* ========================================================= */}
      <Row gutter={[20, 20]} style={{ marginBottom: "24px" }}>
        {/* Left: Configuration Form Card */}
        <Col xs={24} lg={11}>
          <Card
            title={
              <Space>
                <SlidersOutlined style={{ color: "#FC4420" }} />
                <span style={{ color: "#FFF", fontWeight: 700 }}>RTP Engine Core Controls</span>
              </Space>
            }
            extra={
              hasChanges && (
                <Tag color="volcano" style={{ fontWeight: 700 }}>
                  UNSAVED CHANGES
                </Tag>
              )
            }
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: "rgba(252, 68, 32, 0.45)",
              borderRadius: "14px",
              minHeight: "100%",
            }}
            bodyStyle={{ padding: "20px 24px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Master Toggle */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "rgba(252, 68, 32, 0.08)",
                  border: "1px solid rgba(252, 68, 32, 0.3)",
                  padding: "12px 16px",
                  borderRadius: "10px",
                }}
              >
                <div>
                  <Text strong style={{ color: "#FFF", fontSize: "14px" }}>
                    Automated RTP Balancing
                  </Text>
                  <div>
                    <span style={{ fontSize: "12px", color: "#CBD5E1" }}>
                      {settings.enabled
                        ? "Engine automatically maintains profit target on borderline trades"
                        : "Disabled — All trades settle 100% naturally on market ticks"}
                    </span>
                  </div>
                </div>
                <Switch
                  checked={settings.enabled}
                  onChange={(checked) =>
                    setSettings((prev) => ({ ...prev, enabled: checked }))
                  }
                  style={{ backgroundColor: settings.enabled ? "#10B981" : "#475569" }}
                />
              </div>

              {/* Target RTP Slider */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <Text strong style={{ color: "#FFF" }}>
                    Target RTP Percentage (R_target)
                  </Text>
                  <Text strong style={{ color: "#FC4420", fontFamily: "monospace" }}>
                    {settings.targetRtp}% (House Profit: {houseMargin}%)
                  </Text>
                </div>
                <Row gutter={16} align="middle">
                  <Col span={18}>
                    <Slider
                      min={75.0}
                      max={98.0}
                      step={0.1}
                      value={settings.targetRtp}
                      onChange={(val) =>
                        setSettings((prev) => ({ ...prev, targetRtp: val }))
                      }
                      trackStyle={{ backgroundColor: "#FC4420" }}
                      handleStyle={{ borderColor: "#FC4420", backgroundColor: "#FC4420" }}
                    />
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      min={75.0}
                      max={98.0}
                      step={0.1}
                      value={settings.targetRtp}
                      onChange={(val) =>
                        setSettings((prev) => ({ ...prev, targetRtp: val }))
                      }
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                      style={{ width: "100%", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                    />
                  </Col>
                </Row>
                {/* Presets */}
                <Space size={8} style={{ marginTop: "8px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "12px", color: "#CBD5E1", fontWeight: 600 }}>
                    Presets:
                  </span>
                  {[
                    { rtp: 92.0, label: "92% (8% House)" },
                    { rtp: 94.0, label: "94% (6% House)" },
                    { rtp: 96.0, label: "96% (4% House)" },
                  ].map((p) => {
                    const isActive = Number(settings.targetRtp) === p.rtp;
                    return (
                      <Button
                        key={p.rtp}
                        size="small"
                        onClick={() => handleSetPreset(p.rtp)}
                        style={{
                          fontSize: "11px",
                          height: "26px",
                          backgroundColor: isActive ? "#FC4420" : "rgba(252, 68, 32, 0.15)",
                          borderColor: "#FC4420",
                          color: "#FFFFFF",
                          fontWeight: isActive ? 700 : 500,
                          boxShadow: isActive ? "0 0 10px rgba(252, 68, 32, 0.5)" : "none",
                        }}
                      >
                        {p.label}
                      </Button>
                    );
                  })}
                </Space>
              </div>

              {/* Tolerance Margin */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <Text strong style={{ color: "#FFF" }}>
                    Tolerance Corridor Range (±τ)
                  </Text>
                  <Text strong style={{ color: "#38BDF8", fontFamily: "monospace" }}>
                    ±{settings.tolerance}% [{toleranceLower}% ── {toleranceUpper}%]
                  </Text>
                </div>
                <Row gutter={16} align="middle">
                  <Col span={18}>
                    <Slider
                      min={0.5}
                      max={5.0}
                      step={0.1}
                      value={settings.tolerance}
                      onChange={(val) =>
                        setSettings((prev) => ({ ...prev, tolerance: val }))
                      }
                      trackStyle={{ backgroundColor: "#38BDF8" }}
                      handleStyle={{ borderColor: "#38BDF8", backgroundColor: "#38BDF8" }}
                    />
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      min={0.5}
                      max={5.0}
                      step={0.1}
                      value={settings.tolerance}
                      onChange={(val) =>
                        setSettings((prev) => ({ ...prev, tolerance: val }))
                      }
                      formatter={(value) => `±${value}%`}
                      parser={(value) => value.replace("±", "").replace("%", "")}
                      style={{ width: "100%", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Whale Bet Liability Threshold */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <Text strong style={{ color: "#FFF" }}>
                    Whale Bet Liability Limit ($)
                  </Text>
                  <Tooltip title="Single bets at or above this stake will undergo strict house preservation protection during high-RTP regimes.">
                    <InfoCircleOutlined style={{ color: "#FFA07A" }} />
                  </Tooltip>
                </div>
                <InputNumber
                  min={10}
                  max={50000}
                  step={50}
                  value={settings.whaleThreshold}
                  onChange={(val) =>
                    setSettings((prev) => ({ ...prev, whaleThreshold: val }))
                  }
                  formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(val) => val.replace(/\$\s?|(,*)/g, "")}
                  style={{ width: "100%", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                />
              </div>

              {/* Advanced Parameters Accordion */}
              <Collapse
                bordered={false}
                style={{ backgroundColor: "rgba(252, 68, 32, 0.05)", border: "1px solid rgba(252, 68, 32, 0.3)", borderRadius: "8px" }}
                items={[
                  {
                    key: "1",
                    label: (
                      <span style={{ color: "#CBD5E1", fontSize: "12px", fontWeight: 600 }}>
                        Advanced Guardrail Tuning (Cold Start, Cap & Pips)
                      </span>
                    ),
                    children: (
                      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "12px", color: "#FFFFFF", fontWeight: 500 }}>
                              Minimum Sample Size (N_min)
                            </span>
                            <span style={{ color: "#FC4420", fontFamily: "monospace", fontSize: "12px", fontWeight: 700 }}>
                              {settings.minSampleSize} trades
                            </span>
                          </div>
                          <InputNumber
                            min={5}
                            max={500}
                            value={settings.minSampleSize}
                            onChange={(val) =>
                              setSettings((prev) => ({ ...prev, minSampleSize: val }))
                            }
                            style={{ width: "100%", marginTop: "6px", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                          />
                        </div>

                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "12px", color: "#FFFFFF", fontWeight: 500 }}>
                              Max Batch Intervention Cap
                            </span>
                            <span style={{ color: "#FC4420", fontFamily: "monospace", fontSize: "12px", fontWeight: 700 }}>
                              {settings.maxInterventionPercentage}%
                            </span>
                          </div>
                          <InputNumber
                            min={1}
                            max={35}
                            value={settings.maxInterventionPercentage}
                            onChange={(val) =>
                              setSettings((prev) => ({
                                ...prev,
                                maxInterventionPercentage: val,
                              }))
                            }
                            formatter={(v) => `${v}%`}
                            parser={(v) => v.replace("%", "")}
                            style={{ width: "100%", marginTop: "6px", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                          />
                        </div>

                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "12px", color: "#FFFFFF", fontWeight: 500 }}>
                              Borderline Pip Threshold (Δ Borderline)
                            </span>
                            <span style={{ color: "#FC4420", fontFamily: "monospace", fontSize: "12px", fontWeight: 700 }}>
                              {settings.borderlineThresholdPips} pips
                            </span>
                          </div>
                          <InputNumber
                            min={0.5}
                            max={10.0}
                            step={0.1}
                            value={settings.borderlineThresholdPips}
                            onChange={(val) =>
                              setSettings((prev) => ({
                                ...prev,
                                borderlineThresholdPips: val,
                              }))
                            }
                            style={{ width: "100%", marginTop: "6px", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                          />
                        </div>

                        {/* Phase 4: Spin & Multiplier Sync Controls */}
                        <Divider style={{ borderColor: "rgba(252, 68, 32, 0.25)", margin: "8px 0" }} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <span style={{ fontSize: "12px", color: "#FFFFFF", fontWeight: 500, display: "block" }}>
                              Spin & Win Prize Weights Sync
                            </span>
                            <span style={{ fontSize: "11px", color: "#CBD5E1" }}>
                              Dynamically dampens wheel prizes in House Preservation & boosts in Player Retention
                            </span>
                          </div>
                          <Switch
                            checked={settings.spinSyncEnabled}
                            onChange={(val) => setSettings((prev) => ({ ...prev, spinSyncEnabled: val }))}
                            size="small"
                          />
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <span style={{ fontSize: "12px", color: "#FFFFFF", fontWeight: 500, display: "block" }}>
                              Lucky Combo Multipliers Sync
                            </span>
                            <span style={{ fontSize: "11px", color: "#CBD5E1" }}>
                              Scales combo multipliers (2x, 5x, 10x) based on RTP regime
                            </span>
                          </div>
                          <Switch
                            checked={settings.multiplierSyncEnabled}
                            onChange={(val) => setSettings((prev) => ({ ...prev, multiplierSyncEnabled: val }))}
                            size="small"
                          />
                        </div>

                        <Row gutter={12}>
                          <Col span={12}>
                            <span style={{ fontSize: "11px", color: "#FFA07A", fontWeight: 600 }}>
                              Preservation Dampener
                            </span>
                            <InputNumber
                              min={0.30}
                              max={1.00}
                              step={0.05}
                              value={settings.multiplierDampener}
                              onChange={(val) => setSettings((prev) => ({ ...prev, multiplierDampener: val }))}
                              formatter={(v) => `${v}x`}
                              parser={(v) => v.replace("x", "")}
                              style={{ width: "100%", marginTop: "4px", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                            />
                          </Col>
                          <Col span={12}>
                            <span style={{ fontSize: "11px", color: "#FFA07A", fontWeight: 600 }}>
                              Retention Booster
                            </span>
                            <InputNumber
                              min={1.00}
                              max={2.00}
                              step={0.05}
                              value={settings.multiplierBooster}
                              onChange={(val) => setSettings((prev) => ({ ...prev, multiplierBooster: val }))}
                              formatter={(v) => `${v}x`}
                              parser={(v) => v.replace("x", "")}
                              style={{ width: "100%", marginTop: "4px", backgroundColor: "#1f0805", borderColor: "rgba(252, 68, 32, 0.45)", color: "#FFFFFF" }}
                            />
                          </Col>
                        </Row>
                      </div>
                    ),
                  },
                ]}
              />

              {/* Form Action Buttons */}
              <div style={{ display: "flex", gap: "10px", paddingTop: "6px" }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={!hasChanges}
                  onClick={handleSaveSettings}
                  style={{
                    flex: 1,
                    backgroundColor: "#FC4420",
                    borderColor: "#FC4420",
                    fontWeight: 700,
                    boxShadow: "0 0 12px rgba(252, 68, 32, 0.4)",
                  }}
                >
                  Save & Apply Settings
                </Button>
                <Button
                  disabled={!hasChanges || saving}
                  onClick={handleResetSettings}
                  style={{
                    borderColor: "rgba(252, 68, 32, 0.4)",
                    backgroundColor: "rgba(252, 68, 32, 0.1)",
                    color: "#CBD5E1",
                    fontWeight: 600,
                  }}
                >
                  Revert
                </Button>
              </div>
            </div>
          </Card>
        </Col>

        {/* Right: Recharts Convergence Visualizer */}
        <Col xs={24} lg={13}>
          <Card
            title={
              <Space>
                <RiseOutlined style={{ color: "#10B981" }} />
                <span style={{ color: "#FFF", fontWeight: 700 }}>RTP Convergence & Corridor Trajectory</span>
              </Space>
            }
            extra={
              <Tag color="cyan" style={{ fontSize: "11px", fontWeight: 700 }}>
                Target Corridor: {toleranceLower}% - {toleranceUpper}%
              </Tag>
            }
            style={{
              background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
              borderColor: "rgba(252, 68, 32, 0.45)",
              borderRadius: "14px",
              minHeight: "100%",
            }}
            bodyStyle={{ padding: "20px 20px 10px 10px" }}
          >
            {historyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart
                  data={historyData}
                  margin={{ top: 15, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(252, 68, 32, 0.15)" />
                  <XAxis
                    dataKey="time"
                    stroke="#CBD5E1"
                    tick={{ fontSize: 11, fill: "#CBD5E1" }}
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    stroke="#CBD5E1"
                    tick={{ fontSize: 11, fill: "#CBD5E1" }}
                    unit="%"
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "#160503",
                      borderColor: "rgba(252, 68, 32, 0.5)",
                      borderRadius: "8px",
                      color: "#FFF",
                    }}
                  />
                  <Legend verticalAlign="top" height={36} />

                  {/* Target Reference Line */}
                  <ReferenceLine
                    y={targetRtp}
                    stroke="#FC4420"
                    strokeDasharray="4 4"
                    label={{
                      value: `Target: ${targetRtp}%`,
                      fill: "#FC4420",
                      fontSize: 11,
                      position: "right",
                    }}
                  />

                  {/* Tolerance Corridor Reference Lines */}
                  <ReferenceLine
                    y={Number(toleranceLower)}
                    stroke="#38BDF8"
                    strokeDasharray="2 2"
                  />
                  <ReferenceLine
                    y={Number(toleranceUpper)}
                    stroke="#38BDF8"
                    strokeDasharray="2 2"
                  />

                  {/* Realized RTP Line */}
                  <Line
                    type="monotone"
                    dataKey="realizedRtp"
                    name="Realized RTP (%)"
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ fill: "#10B981", r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  height: 380,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  color: "#CBD5E1",
                }}
              >
                <DashboardOutlined style={{ fontSize: "40px", marginBottom: "12px", color: "#FC4420" }} />
                <span style={{ fontSize: "14px", fontWeight: 600, color: "#FFFFFF" }}>
                  No historical snapshots recorded yet in this window.
                </span>
                <span style={{ fontSize: "12px", color: "#94A3B8", marginTop: "4px" }}>
                  Snapshots accumulate automatically as trades settle.
                </span>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ========================================================= */}
      {/* 4. SETTLEMENT AUDIT TRAIL TABLE */}
      {/* ========================================================= */}
      <Card
        className="trade-pattern-table-card"
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <Space>
              <CheckCircleOutlined style={{ color: "#FC4420" }} />
              <span style={{ color: "#FFF", fontWeight: 700 }}>
                Settlement Decision Audit Trail & Borderline Inspector
              </span>
            </Space>

            <Space wrap>
              <Radio.Group
                value={auditFilter}
                onChange={(e) => handleAuditFilterChange(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">All Settled</Radio.Button>
                <Radio.Button value="adjusted">Adjusted Only</Radio.Button>
                <Radio.Button value="borderline">Borderline Only</Radio.Button>
                <Radio.Button value="whale">Whale Bets Only</Radio.Button>
              </Radio.Group>

              <Button
                size="small"
                icon={<SyncOutlined spin={auditLoading} />}
                onClick={() => handleAuditFilterChange(auditFilter)}
                style={{
                  backgroundColor: "rgba(252, 68, 32, 0.15)",
                  borderColor: "rgba(252, 68, 32, 0.45)",
                  color: "#FFFFFF",
                }}
              />
            </Space>
          </div>
        }
        style={{
          background: "linear-gradient(180deg, rgba(252, 68, 32, 0.12) 0%, rgba(20, 5, 3, 0.95) 100%)",
          borderColor: "rgba(252, 68, 32, 0.45)",
          borderRadius: "14px",
        }}
        bodyStyle={{ padding: "0" }}
      >
        <Table
          className="table-clr"
          columns={auditColumns}
          dataSource={auditTrades}
          rowKey={(r) => r._id || r.clientOrderId || Math.random()}
          loading={auditLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            className: "pagination-clr",
            showTotal: (total) => `Total ${total} audit records`,
          }}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <div style={{ padding: "32px", color: "#CBD5E1", textAlign: "center" }}>
                No settled trades matching filter "{auditFilter}".
              </div>
            ),
          }}
          style={{ backgroundColor: "transparent" }}
        />
      </Card>
    </div>
  );
};

export default RtpEngineSettings;
